import { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw, Check, AlertTriangle, ArrowRight, ArrowLeftRight,
  Plus, Trash2, Search, ServerCrash, Phone, Info,
} from 'lucide-react';
import { fetchDids }        from '../services/voxboneApi';
import {
  listPlatforms, getRoutingProfiles, saveRoutingProfile, activateRouting,
  reconcileRouting, syncDids, listSites, listSbcs, importRoutingFromSbc,
} from '../services/portalApi';

const PSTN = '__pstn__';
const LOCAL_DIDS_KEY = 'vx_local_dids_v2';

function loadLocal(key, fallback) {
  try { return JSON.parse(localStorage.getItem(key) || 'null') ?? fallback; }
  catch { return fallback; }
}
function saveLocal(key, val) { localStorage.setItem(key, JSON.stringify(val)); }

// ── Small components ──────────────────────────────────────────────────────────

function RouteBadge({ type }) {
  if (type === 'primary')     return <span className="badge badge-green">Primary</span>;
  if (type === 'alternative') return <span className="badge badge-amber">Alternative</span>;
  return <span className="badge badge-gray">Not set</span>;
}

function Spinner() {
  return <RefreshCw size={13} style={{ animation: 'spin 0.8s linear infinite' }} />;
}

function PstnInput({ value, onCommit }) {
  const [draft, setDraft] = useState(value || '');
  const [err,   setErr]   = useState('');
  useEffect(() => { setDraft(value || ''); }, [value]);

  const commit = () => {
    if (!/^\+[1-9]\d{6,14}$/.test(draft)) {
      setErr('Must be E.164 format, e.g. +441234567890');
      return;
    }
    setErr('');
    onCommit(draft);
  };

  return (
    <div style={{ marginTop: 4 }}>
      <div style={{ display: 'flex', gap: 4 }}>
        <input
          type="tel"
          placeholder="+441234567890"
          value={draft}
          onChange={e => { setDraft(e.target.value); setErr(''); }}
          onKeyDown={e => e.key === 'Enter' && commit()}
          style={{ fontSize: 11, padding: '3px 8px', flex: 1, maxWidth: 160, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text-2)', outline: 'none' }}
        />
        <button
          onClick={commit}
          title="Save number"
          style={{ background: 'var(--ok-bg)', border: '1px solid #16653488', borderRadius: 4, color: 'var(--ok)', padding: '3px 7px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
        >
          <Check size={11}/>
        </button>
      </div>
      {err   && <div style={{ fontSize: 10, color: 'var(--err)', marginTop: 2 }}>{err}</div>}
      {!err && value && <div style={{ fontSize: 10, color: 'var(--muted-3)', marginTop: 2 }}>✓ {value}</div>}
    </div>
  );
}

// ── Import routing from an SBC dial plan ─────────────────────────────────────
function ImportFromSbcModal({ onClose, onApplied }) {
  const [sbcs,    setSbcs]    = useState([]);
  const [sbcId,   setSbcId]   = useState('');
  const [preview, setPreview] = useState(null);
  const [busy,    setBusy]    = useState(false);
  const [err,     setErr]     = useState('');

  useEffect(() => { listSbcs().then(d => setSbcs((d.sbcs || []).filter(s => s.api_url))).catch(() => {}); }, []);

  async function runPreview() {
    if (!sbcId) return;
    setBusy(true); setErr(''); setPreview(null);
    try { setPreview(await importRoutingFromSbc(Number(sbcId), false)); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }
  async function apply() {
    setBusy(true); setErr('');
    try {
      const r = await importRoutingFromSbc(Number(sbcId), true);
      onApplied(`Imported ${r.counts.applied} routing change${r.counts.applied === 1 ? '' : 's'} from ${r.sbc}`);
    } catch (e) { setErr(e.message); setBusy(false); }
  }

  const changes = (preview?.matched || []).filter(m => m.changed);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 640, maxHeight: '100%', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <div className="modal-title">Import routing from SBC dial plan</div>
          <button className="btn btn-secondary btn-xs" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body" style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
          <div className="alert alert-info"><Info size={14} style={{ flexShrink: 0 }}/>
            <span>Reads the SBC's live dial plan and matches each number's tag to a platform, so Number Routing reflects what the SBC actually has. Preview first, then apply.</span>
          </div>
          <div className="form-group">
            <label className="form-label">SBC</label>
            <select className="form-input select-input" value={sbcId} onChange={e => { setSbcId(e.target.value); setPreview(null); }}>
              <option value="">— Select an SBC —</option>
              {sbcs.map(s => <option key={s.id} value={s.id}>{s.name}{s.fqdn ? ` (${s.fqdn})` : ''}</option>)}
            </select>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={runPreview} disabled={!sbcId || busy}>
            {busy && !preview ? 'Reading dial plan…' : 'Preview'}
          </button>
          {err && <div style={{ color: 'var(--err)', fontSize: 12, marginTop: 10 }}>{err}</div>}

          {preview && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>
                {preview.counts.rules} rules · <strong style={{ color: 'var(--ok)' }}>{changes.length} to change</strong> ·
                {' '}{preview.counts.matched - changes.length} already correct ·
                {' '}{preview.counts.ambiguous} ambiguous · {preview.counts.unmatched} unmatched
              </div>
              {changes.length > 0 ? (
                <table className="data-table" style={{ width: '100%', fontSize: 12 }}>
                  <thead><tr><th style={{ textAlign: 'left' }}>Number</th><th style={{ textAlign: 'left' }}>Tag</th><th style={{ textAlign: 'left' }}>→ Platform</th><th style={{ textAlign: 'left' }}>Was</th></tr></thead>
                  <tbody>
                    {changes.slice(0, 200).map(m => (
                      <tr key={m.didId}>
                        <td className="font-mono">{m.e164}</td>
                        <td className="font-mono" style={{ color: 'var(--muted)' }}>{m.tag}</td>
                        <td style={{ color: 'var(--ok)' }}>{m.platformName}{m.customerName ? ` · ${m.customerName}` : ''}</td>
                        <td style={{ color: 'var(--muted-3)' }}>{m.currentPlatformName || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <div style={{ fontSize: 12, color: 'var(--muted-3)' }}>Nothing to change — routing already matches the dial plan.</div>}
              {preview.counts.unmatched > 0 && (
                <div style={{ fontSize: 11, color: 'var(--muted-3)', marginTop: 8 }}>
                  {preview.counts.unmatched} rule(s) had no matching platform tag (unknown number or a tag no platform is configured with) — left untouched.
                </div>
              )}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={apply} disabled={busy || !preview || changes.length === 0}>
            <Check size={13}/> {busy && preview ? 'Applying…' : `Apply ${changes.length} change${changes.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function VoxboneRouting({ viewAsCustomer, user }) {
  const [importOpen, setImportOpen] = useState(false);
  const [platforms,      setPlatforms]      = useState([]);
  const [dids,           setDids]           = useState(() => loadLocal(LOCAL_DIDS_KEY, []));
  const [profiles,       setProfiles]       = useState({});
  const [loading,        setLoading]        = useState(true);
  const [apiError,       setApiError]       = useState(null);
  const [sandboxMode,    setSandboxMode]    = useState(false);
  const [syncingVoxbone, setSyncingVoxbone] = useState(false);
  const [syncVxResult,   setSyncVxResult]   = useState(null);

  const [sites,       setSites]       = useState([]);
  const [filterSite,  setFilterSite]  = useState('all');

  const [globalPrimary, setGlobalPrimary] = useState('');
  const [globalAlt,     setGlobalAlt]     = useState('');

  const [selected,    setSelected]    = useState(new Set());
  const [search,      setSearch]      = useState('');
  const [filterRoute, setFilterRoute] = useState('all');
  const [pushing,     setPushing]     = useState(false);
  const [pushResult,  setPushResult]  = useState(null);

  const [showAddDid, setShowAddDid] = useState(false);
  const [newDid,     setNewDid]     = useState('');
  const [newDidDesc, setNewDidDesc] = useState('');

  const load_ = useCallback(async () => {
    setLoading(true);
    setApiError(null);
    try {
      // Load platforms, DIDs, and sites in parallel; then reconcile routing
      // from the local dids.voice_uri_id cache before reading profiles,
      // so the active_route always reflects Voxbone's current state.
      const [pData, liveDids, sitesData] = await Promise.all([
        listPlatforms(viewAsCustomer?.id),
        fetchDids(),
        listSites().catch(() => ({ sites: [] })),
      ]);
      // Only offer platforms enabled for the viewed customer (admin unscoped →
      // all are flagged enabled). Keeps the routing dropdowns tidy per customer.
      setPlatforms((pData.platforms || []).filter(p => p.enabled !== false));
      setSites(sitesData.sites || []);
      if (liveDids.length > 0) {
        setDids(liveDids);
        saveLocal(LOCAL_DIDS_KEY, liveDids);
        setSandboxMode(false);
      } else {
        setDids(loadLocal(LOCAL_DIDS_KEY, []));
        setSandboxMode(true);
      }
      // Reconcile active_route from cached voice_uri_id, then read fresh profiles
      await reconcileRouting().catch(() => null);
      const routingData = await getRoutingProfiles();
      setProfiles(routingData.routing || {});
    } catch (e) {
      setApiError(e.message);
    } finally {
      setLoading(false);
    }
  }, [viewAsCustomer]);

  // Pull fresh DIDs from Voxbone (admin only), then reconcile and reload profiles
  const handleSyncFromVoxbone = useCallback(async () => {
    setSyncingVoxbone(true);
    setSyncVxResult(null);
    try {
      const syncRes = await syncDids();
      const routingData = await getRoutingProfiles();
      setProfiles(routingData.routing || {});
      // Also refresh DID list from local DB (now updated by sync)
      const liveDids = await fetchDids().catch(() => []);
      if (liveDids.length > 0) { setDids(liveDids); saveLocal(LOCAL_DIDS_KEY, liveDids); }
      setSyncVxResult({
        type: 'success',
        message: `${syncRes.count} DIDs synced · ${syncRes.reconciled ?? 0} routes reconciled`,
      });
    } catch (e) {
      setSyncVxResult({ type: 'warn', message: `Sync failed: ${e.message}` });
    } finally {
      setSyncingVoxbone(false);
    }
  }, []);

  useEffect(() => { load_(); }, [load_]);

  // Update a profile and save to server immediately (optimistic)
  const updateProfile = useCallback((didId, updates) => {
    const id = String(didId);
    setProfiles(prev => {
      const merged = { ...(prev[id] || {}), ...updates };
      saveRoutingProfile(id, {
        primaryPlatformId: merged.primaryPlatformId ?? null,
        primaryPstnE164:   merged.primaryPstnE164   ?? '',
        altPlatformId:     merged.altPlatformId     ?? null,
        altPstnE164:       merged.altPstnE164       ?? '',
      }).catch(e => console.error('[routing] save failed:', e.message));
      return { ...prev, [id]: merged };
    });
  }, []);

  // Drop-down value helpers
  const primaryVal = (p) => {
    if (!p) return '';
    if (p.primaryPlatformId) return String(p.primaryPlatformId);
    if (p.primaryPstnE164 || p._pstnPrimary) return PSTN;
    return '';
  };
  const altVal = (p) => {
    if (!p) return '';
    if (p.altPlatformId) return String(p.altPlatformId);
    if (p.altPstnE164 || p._pstnAlt) return PSTN;
    return '';
  };

  const onPrimaryChange = (didId, val) => {
    if (val === '')        updateProfile(didId, { primaryPlatformId: null, primaryPstnE164: '', _pstnPrimary: false });
    else if (val === PSTN) updateProfile(didId, { primaryPlatformId: null, _pstnPrimary: true });
    else                   updateProfile(didId, { primaryPlatformId: Number(val), primaryPstnE164: '', _pstnPrimary: false });
  };
  const onAltChange = (didId, val) => {
    if (val === '')        updateProfile(didId, { altPlatformId: null, altPstnE164: '', _pstnAlt: false });
    else if (val === PSTN) updateProfile(didId, { altPlatformId: null, _pstnAlt: true });
    else                   updateProfile(didId, { altPlatformId: Number(val), altPstnE164: '', _pstnAlt: false });
  };

  const getActiveRoute = (did) => profiles[String(did.didId)]?.activeRoute || 'unset';

  const visibleDids = viewAsCustomer
    ? dids.filter(d => d.customerId === viewAsCustomer.id || d.customerId === String(viewAsCustomer.id))
    : dids;

  const siteFiltered = filterSite === 'all' ? visibleDids
    : filterSite === 'none' ? visibleDids.filter(d => !d.siteId)
    : visibleDids.filter(d => String(d.siteId) === filterSite || d.siteId === Number(filterSite));

  const filtered = siteFiltered.filter(d => {
    if (search) {
      const q = search.toLowerCase();
      if (!d.e164?.toLowerCase().includes(q) && !d.description?.toLowerCase().includes(q)) return false;
    }
    if (filterRoute !== 'all') {
      const r = getActiveRoute(d);
      if (filterRoute === 'unset' && r !== 'unset') return false;
      if (filterRoute !== 'unset' && r !== filterRoute) return false;
    }
    return true;
  });

  const toggleSelect = (id) =>
    setSelected(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  const toggleAll = () =>
    setSelected(selected.size === filtered.length && filtered.length > 0
      ? new Set() : new Set(filtered.map(d => d.didId)));

  const applyGlobalDefaults = () => {
    if (!globalPrimary && !globalAlt) return;
    filtered.forEach(d => {
      const updates = {};
      if (globalPrimary) {
        if (globalPrimary === PSTN) { updates.primaryPlatformId = null; updates._pstnPrimary = true; }
        else { updates.primaryPlatformId = Number(globalPrimary); updates.primaryPstnE164 = ''; updates._pstnPrimary = false; }
      }
      if (globalAlt) {
        if (globalAlt === PSTN) { updates.altPlatformId = null; updates._pstnAlt = true; }
        else { updates.altPlatformId = Number(globalAlt); updates.altPstnE164 = ''; updates._pstnAlt = false; }
      }
      updateProfile(d.didId, updates);
    });
  };

  const activateRoute = async (routeType) => {
    setPushResult(null);
    const targetIds = selected.size > 0 ? [...selected] : filtered.map(d => d.didId);

    const toActivate = targetIds.filter(id => {
      const p = profiles[String(id)];
      return routeType === 'primary'
        ? (p?.primaryPlatformId || p?.primaryPstnE164)
        : (p?.altPlatformId     || p?.altPstnE164);
    });

    if (!toActivate.length) {
      setPushResult({ type: 'warn', message: `No ${routeType} route configured for selected numbers.` });
      return;
    }

    setPushing(true);
    try {
      const result = await activateRouting(toActivate, routeType);
      const applied = result.applied || 0;
      const errs    = result.errors  || [];

      if (result.ok) {
        setProfiles(prev => {
          const next = { ...prev };
          toActivate.forEach(id => {
            const sid = String(id);
            if (!errs.some(e => e.startsWith(`DID ${id}:`))) {
              next[sid] = { ...(next[sid] || {}), activeRoute: routeType };
            }
          });
          return next;
        });
      }

      try { const fresh = await fetchDids(); if (fresh.length) { setDids(fresh); saveLocal(LOCAL_DIDS_KEY, fresh); } } catch {}

      const note = errs.length ? ` (${errs.length} failed)` : '';
      setPushResult(errs.length === 0
        ? { type: 'success', message: `${applied} number${applied !== 1 ? 's' : ''} switched to ${routeType}${note}.` }
        : { type: 'warn',    message: `${applied} ok${note}: ${errs.slice(0, 2).join('; ')}${errs.length > 2 ? '…' : ''}` });
    } catch (e) {
      setPushResult({ type: 'error', message: e.message });
    } finally {
      setPushing(false);
    }
  };

  const addDid = () => {
    if (!newDid.trim()) return;
    const id    = `local-${Date.now()}`;
    const entry = { didId: id, e164: newDid.trim(), description: newDidDesc.trim(), voiceUriId: null, countryCodeA3: '' };
    const next  = [...dids, entry];
    setDids(next);
    saveLocal(LOCAL_DIDS_KEY, next);
    setNewDid(''); setNewDidDesc(''); setShowAddDid(false);
  };

  const removeDid = (didId) => {
    const next = dids.filter(d => d.didId !== didId);
    setDids(next);
    saveLocal(LOCAL_DIDS_KEY, next);
    setSelected(prev => { const s = new Set(prev); s.delete(didId); return s; });
  };

  const primaryCount = visibleDids.filter(d => getActiveRoute(d) === 'primary').length;
  const altCount     = visibleDids.filter(d => getActiveRoute(d) === 'alternative').length;

  // ── Loading / error states ────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="content">
        <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
          {['Total DIDs','On Primary','On Alternative','Platforms'].map(l => (
            <div key={l} className="stat-card">
              <div className="stat-label">{l}</div>
              <div className="stat-value" style={{ color: 'var(--surface-2)' }}>—</div>
            </div>
          ))}
        </div>
        <div className="card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 56, gap: 12 }}>
          <Spinner /><span style={{ color: 'var(--muted-3)' }}>Loading routing data…</span>
        </div>
      </div>
    );
  }

  if (apiError) {
    return (
      <div className="content">
        <div className="card" style={{ display:'flex', flexDirection:'column', alignItems:'center', padding:'56px 24px', gap:16 }}>
          <ServerCrash size={44} color="var(--brand-dark)" />
          <div style={{ color:'var(--text)', fontWeight:600 }}>Could not load routing data</div>
          <div style={{ color:'var(--muted-2)', fontSize:13, maxWidth:480, textAlign:'center' }}>{apiError}</div>
          <div className="alert alert-warn" style={{ maxWidth:480, marginBottom:0 }}>
            <AlertTriangle size={14} style={{ flexShrink:0 }} />
            <span>Ensure the proxy server is running (<code>npm start</code> in <code>server/</code>) and <code>.env</code> has a valid <code>VOXBONE_API_KEY</code>.</span>
          </div>
          <button className="btn btn-primary" onClick={load_}><RefreshCw size={13}/> Retry</button>
        </div>
      </div>
    );
  }

  // ── Main UI ───────────────────────────────────────────────────────────────

  const platformOptions = platforms.map(p => ({ value: String(p.id), label: p.name }));

  return (
    <div className="content">
      {importOpen && (
        <ImportFromSbcModal
          onClose={() => setImportOpen(false)}
          onApplied={(msg) => { setImportOpen(false); setSyncVxResult({ type: 'success', message: msg }); load_(); }}
        />
      )}

      {/* Stats */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
        {[
          { label: 'Total DIDs',       value: visibleDids.length, color: 'var(--info)' },
          { label: 'On Primary Route', value: primaryCount,      color: 'var(--ok)' },
          { label: 'On Alternative',   value: altCount,          color: 'var(--warn)' },
          { label: 'Platforms',        value: platforms.length,  color: 'var(--purple)' },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-label">{s.label}</div>
            <div className="stat-value" style={{ color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Customer view banner */}
      {viewAsCustomer && (
        <div className="alert alert-info" style={{ marginBottom: 16, borderColor: '#2563eb44', background: 'var(--info-bg)' }}>
          <Info size={14} style={{ flexShrink: 0, color: 'var(--info)' }} />
          <span style={{ color: 'var(--info)' }}>
            Showing numbers assigned to <strong style={{ color: 'var(--info)' }}>{viewAsCustomer.name}</strong> — {visibleDids.length} number{visibleDids.length !== 1 ? 's' : ''}.
            Switch back to "All customers" in the sidebar to see the full inventory.
          </span>
        </div>
      )}

      {/* Sandbox notice */}
      {sandboxMode && (
        <div className="alert alert-info" style={{ marginBottom: 16 }}>
          <Info size={14} style={{ flexShrink: 0 }} />
          <span>
            <strong>Sandbox mode</strong> — DID inventory is not available from Voxbone.
            Add your numbers manually below, or switch <code>VOXBONE_API_BASE</code> to
            a production endpoint to load live DIDs. Routing changes are saved to the server but
            Voxbone activation is skipped for manual numbers.
          </span>
        </div>
      )}

      {/* Global defaults */}
      <div className="card mb-16">
        <div className="card-header">
          <div>
            <div className="card-title">Default Route Settings</div>
            <div className="card-sub">Apply a platform to all numbers at once — individual overrides still apply per number</div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {syncVxResult && (
              <span style={{
                fontSize: 11, padding: '3px 10px', borderRadius: 6,
                background: syncVxResult.type === 'success' ? 'var(--ok-bg)' : 'var(--warn-bg)',
                color:      syncVxResult.type === 'success' ? 'var(--ok)'   : 'var(--warn)',
                border:     `1px solid ${syncVxResult.type === 'success' ? '#16653444' : 'var(--warn-border)'}`,
              }}>{syncVxResult.message}</span>
            )}
            {user?.isAdmin && (
              <button
                className="btn btn-secondary btn-sm"
                onClick={handleSyncFromVoxbone}
                disabled={syncingVoxbone || loading}
                title="Pull current DID voice URIs from Voxbone and update routing state"
                style={{ color: 'var(--purple)', borderColor: 'var(--purple-border)' }}
              >
                <RefreshCw size={12} style={syncingVoxbone ? { animation: 'spin 0.8s linear infinite' } : undefined}/>
                {syncingVoxbone ? 'Syncing…' : 'Sync from Voxbone'}
              </button>
            )}
            {user?.isAdmin && (
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setImportOpen(true)}
                disabled={loading}
                title="Read an SBC's dial plan and set each number's routing to the matching platform"
              >
                <ArrowRight size={12}/> Import from SBC
              </button>
            )}
            <button className="btn btn-secondary btn-sm" onClick={load_} disabled={loading}>
              <RefreshCw size={12} style={loading ? { animation: 'spin 0.8s linear infinite' } : undefined}/> Refresh
            </button>
          </div>
        </div>
        <div style={{ padding: '16px 20px' }}>
          <div style={{ display:'flex', alignItems:'flex-end', gap:16, flexWrap:'wrap' }}>
            <div className="form-group" style={{ flex:1, minWidth:220, marginBottom:0 }}>
              <label className="form-label" style={{ display:'flex', alignItems:'center', gap:6 }}>
                <span style={{ width:8, height:8, borderRadius:'50%', background:'var(--ok)', boxShadow:'0 0 6px var(--ok)', display:'inline-block' }}/>
                Default Primary Platform
              </label>
              <select className="form-input select-input" value={globalPrimary} onChange={e => setGlobalPrimary(e.target.value)}>
                <option value="">— Select platform —</option>
                <option value={PSTN}>📞 Route to PSTN</option>
                {platformOptions.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            <div style={{ paddingBottom:10, color:'var(--surface-2)' }}><ArrowLeftRight size={18}/></div>
            <div className="form-group" style={{ flex:1, minWidth:220, marginBottom:0 }}>
              <label className="form-label" style={{ display:'flex', alignItems:'center', gap:6 }}>
                <span style={{ width:8, height:8, borderRadius:'50%', background:'var(--warn)', boxShadow:'0 0 6px var(--warn)', display:'inline-block' }}/>
                Default Alternative Platform
              </label>
              <select className="form-input select-input" value={globalAlt} onChange={e => setGlobalAlt(e.target.value)}>
                <option value="">— Select platform —</option>
                <option value={PSTN}>📞 Route to PSTN</option>
                {platformOptions.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            <div style={{ paddingBottom:10 }}>
              <button className="btn btn-primary btn-sm" onClick={applyGlobalDefaults} disabled={(!globalPrimary && !globalAlt) || filtered.length === 0}>
                <Check size={13}/> Apply to {filtered.length < dids.length ? `${filtered.length} Filtered` : 'All'} Numbers
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* DID Routing Table */}
      <div className="card">
        <div className="card-header" style={{ flexWrap:'wrap', gap:10 }}>
          <div>
            <div className="card-title">Number Routing Table</div>
            <div className="card-sub">
              {visibleDids.length} numbers{sandboxMode ? ' (manually managed)' : ' (live from Voxbone)'}
              {viewAsCustomer ? ` · filtered to ${viewAsCustomer.name}` : ''} · {selected.size > 0 ? `${selected.size} selected` : 'none selected'}
            </div>
          </div>
          <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
            {pushResult && (
              <span style={{
                fontSize:12, padding:'4px 12px', borderRadius:6,
                background: pushResult.type === 'success' ? 'var(--ok-bg)' : 'var(--warn-bg)',
                color:      pushResult.type === 'success' ? 'var(--ok)'   : 'var(--warn)',
                border:     `1px solid ${pushResult.type === 'success' ? '#16653444' : 'var(--warn-border)'}`,
              }}>{pushResult.message}</span>
            )}
            <button
              className="btn btn-sm"
              style={{ background:'#0d2218', color:'var(--ok)', border:'1px solid #16653488' }}
              onClick={() => activateRoute('primary')}
              disabled={pushing}
            >
              {pushing ? <Spinner /> : <Check size={12}/>}
              Activate Primary{selected.size > 0 ? ` (${selected.size})` : ''}
            </button>
            <button
              className="btn btn-sm"
              style={{ background:'#1c1000', color:'var(--warn)', border:'1px solid var(--warn-border)' }}
              onClick={() => activateRoute('alternative')}
              disabled={pushing}
            >
              {pushing ? <Spinner /> : <ArrowRight size={12}/>}
              Activate Alternative{selected.size > 0 ? ` (${selected.size})` : ''}
            </button>
            <button className="btn btn-primary btn-sm" onClick={() => setShowAddDid(true)}>
              <Plus size={12}/> Add DID
            </button>
          </div>
        </div>

        {/* Filters */}
        <div style={{ padding:'12px 20px', borderBottom:'1px solid var(--border)' }}>
          <div className="filter-row" style={{ marginBottom:0 }}>
            <div className="search-bar">
              <Search size={14} color="var(--muted-3)"/>
              <input placeholder="Search number or description…" value={search} onChange={e => setSearch(e.target.value)}/>
            </div>
            <select className="select-input" value={filterRoute} onChange={e => setFilterRoute(e.target.value)}>
              <option value="all">All</option>
              <option value="primary">Primary Active</option>
              <option value="alternative">Alternative Active</option>
              <option value="unset">Not Configured</option>
            </select>
            <select className="select-input" value={filterSite} onChange={e => setFilterSite(e.target.value)}>
              <option value="all">All Sites</option>
              <option value="none">No Site</option>
              {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <span style={{ fontSize:12, color:'var(--muted-3)', marginLeft:'auto' }}>{filtered.length} of {dids.length}</span>
          </div>
        </div>

        {/* Table */}
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th style={{ width:40, textAlign:'center' }}>
                  <input type="checkbox"
                    checked={selected.size === filtered.length && filtered.length > 0}
                    ref={el => { if (el) el.indeterminate = selected.size > 0 && selected.size < filtered.length; }}
                    onChange={toggleAll} style={{ cursor:'pointer' }}/>
                </th>
                <th>DID Number</th>
                <th>Description</th>
                <th>Site</th>
                <th>Primary Platform</th>
                <th>Alternative Platform</th>
                <th>Active Route</th>
                {sandboxMode && <th style={{ width:40 }}></th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map(d => {
                const p  = profiles[String(d.didId)] || {};
                const pv = primaryVal(p);
                const av = altVal(p);
                return (
                  <tr key={d.didId} style={{ background: selected.has(d.didId) ? 'rgba(71,6,30,0.12)' : undefined }}>
                    <td style={{ textAlign:'center' }}>
                      <input type="checkbox" checked={selected.has(d.didId)} onChange={() => toggleSelect(d.didId)} style={{ cursor:'pointer' }}/>
                    </td>
                    <td>
                      <span className="tag font-mono">{d.e164}</span>
                      {d.teamsUserName && (
                        <div style={{ fontSize: 10, color: 'var(--info)', marginTop: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span style={{ fontWeight: 800, fontSize: 8, background: '#0d2b4e', padding: '1px 3px', borderRadius: 3 }}>MT</span>
                          {d.teamsUserName}
                        </div>
                      )}
                      {d.zoomUserName && (
                        <div style={{ fontSize: 10, color: '#2d8cff', marginTop: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span style={{ fontWeight: 800, fontSize: 8, background: '#1b2c40', padding: '1px 3px', borderRadius: 3 }}>ZM</span>
                          {d.zoomUserName}
                        </div>
                      )}
                    </td>
                    <td style={{ color:'var(--muted-2)', fontSize:12 }}>{d.description || d.countryCodeA3 || '—'}</td>
                    <td>
                      {d.siteName
                        ? (
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 500,
                            background: 'var(--ok-bg)', color: 'var(--cyan)', border: '1px solid #0891b244',
                          }}>
                            {d.siteName}
                          </span>
                        )
                        : <span style={{ color: 'var(--muted-3)', fontSize: 12 }}>—</span>
                      }
                    </td>
                    <td>
                      <select
                        className="select-input"
                        style={{ fontSize:11, padding:'3px 8px', maxWidth:200 }}
                        value={pv}
                        onChange={e => onPrimaryChange(d.didId, e.target.value)}
                      >
                        <option value="">— None —</option>
                        <option value={PSTN}>📞 Route to PSTN</option>
                        {platformOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                      </select>
                      {pv === PSTN && (
                        <PstnInput
                          value={p.primaryPstnE164 || ''}
                          onCommit={e164 => updateProfile(d.didId, { primaryPstnE164: e164 })}
                        />
                      )}
                    </td>
                    <td>
                      <select
                        className="select-input"
                        style={{ fontSize:11, padding:'3px 8px', maxWidth:200 }}
                        value={av}
                        onChange={e => onAltChange(d.didId, e.target.value)}
                      >
                        <option value="">— None —</option>
                        <option value={PSTN}>📞 Route to PSTN</option>
                        {platformOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                      </select>
                      {av === PSTN && (
                        <PstnInput
                          value={p.altPstnE164 || ''}
                          onCommit={e164 => updateProfile(d.didId, { altPstnE164: e164 })}
                        />
                      )}
                    </td>
                    <td><RouteBadge type={getActiveRoute(d)}/></td>
                    {sandboxMode && (
                      <td>
                        <button className="btn btn-secondary btn-xs" style={{ color:'var(--err)' }} onClick={() => removeDid(d.didId)}>
                          <Trash2 size={10}/>
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={sandboxMode ? 8 : 7} style={{ textAlign:'center', color:'var(--muted-3)', padding:'40px 0' }}>
                    <Phone size={24} color="var(--border)" style={{ marginBottom:8, display:'block', margin:'0 auto 8px' }}/>
                    {dids.length === 0 ? 'No DIDs yet — click Add DID to get started' : 'No DIDs match the current filter'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div style={{ padding:'10px 20px', borderTop:'1px solid var(--border)', display:'flex', alignItems:'center', gap:8 }}>
          <AlertTriangle size={12} color="var(--muted-3)"/>
          <span style={{ fontSize:11, color:'var(--muted-3)' }}>
            Platform selections are saved automatically.{' '}
            <strong style={{ color:'var(--muted-2)' }}>Activate</strong> pushes to Voxbone and persists until changed.{' '}
            <strong style={{ color:'var(--muted-2)' }}>Sync from Voxbone</strong> pulls the current voice URI state back into the portal (bidirectional).
            Configure platform SIP URLs in <strong style={{ color:'var(--muted-2)' }}>UC / CC Platforms</strong>.
          </span>
        </div>
      </div>

      {/* Add DID modal */}
      {showAddDid && (
        <div className="modal-overlay" onClick={() => setShowAddDid(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Add DID Number</div>
              <button className="btn btn-secondary btn-xs" onClick={() => setShowAddDid(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">E.164 Number</label>
                <input className="form-input font-mono" placeholder="+441234567890"
                  value={newDid} onChange={e => setNewDid(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addDid()}/>
              </div>
              <div className="form-group" style={{ marginBottom:0 }}>
                <label className="form-label">Description (optional)</label>
                <input className="form-input" placeholder="e.g. London HQ"
                  value={newDidDesc} onChange={e => setNewDidDesc(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addDid()}/>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAddDid(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={addDid} disabled={!newDid.trim()}>
                <Plus size={13}/> Add Number
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
